import { TrendingUp, Users, Zap, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useRolloutProgress } from '../../hooks/useFeatureFlags';

interface RolloutProgressTabProps {}

export default function RolloutProgressTab({}: RolloutProgressTabProps) {
  const { progress, loading, error } = useRolloutProgress();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  const featureRollouts = progress || [
    {
      feature: 'Observability (Tracing & Metrics)',
      phase: 'Phase 0',
      enabled: 1,
      total: 150,
      percentage: 0.7,
      status: 'canary',
      color: 'from-blue-600 to-cyan-600',
    },
    {
      feature: 'Redis Cache',
      phase: 'Phase 1',
      enabled: 0,
      total: 150,
      percentage: 0,
      status: 'planned',
      color: 'from-green-600 to-teal-600',
    },
    {
      feature: 'Circuit Breaker',
      phase: 'Phase 2',
      enabled: 0,
      total: 150,
      percentage: 0,
      status: 'planned',
      color: 'from-orange-600 to-red-600',
    },
    {
      feature: 'Cloud Tasks Fan-Out',
      phase: 'Phase 3',
      enabled: 0,
      total: 150,
      percentage: 0,
      status: 'planned',
      color: 'from-purple-600 to-pink-600',
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'rolling':
        return <TrendingUp className="w-5 h-5 text-blue-400" />;
      case 'canary':
        return <Zap className="w-5 h-5 text-yellow-400" />;
      case 'planned':
        return <Clock className="w-5 h-5 text-slate-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'rolling':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'canary':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'planned':
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
      default:
        return 'bg-slate-500/20 text-slate-400 border-slate-500/30';
    }
  };

  return (
    <div className="space-y-6">
      {/* Overall Progress */}
      <div className="relative overflow-hidden rounded-xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50">
        <div className="px-8 py-6">
          <h3 className="text-xl font-semibold text-white mb-4">Overall Rollout Status</h3>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 p-6 shadow-2xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full transform translate-x-12 -translate-y-12"></div>
              <div className="relative">
                <div className="text-white opacity-70 text-sm font-medium mb-1">Total Companies</div>
                <div className="text-4xl font-bold text-white">150</div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-green-500 to-teal-600 p-6 shadow-2xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full transform translate-x-12 -translate-y-12"></div>
              <div className="relative">
                <div className="text-white opacity-70 text-sm font-medium mb-1">With Features</div>
                <div className="text-4xl font-bold text-white">1</div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600 p-6 shadow-2xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full transform translate-x-12 -translate-y-12"></div>
              <div className="relative">
                <div className="text-white opacity-70 text-sm font-medium mb-1">Canary</div>
                <div className="text-4xl font-bold text-white">1</div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 p-6 shadow-2xl">
              <div className="absolute top-0 right-0 w-24 h-24 bg-white opacity-10 rounded-full transform translate-x-12 -translate-y-12"></div>
              <div className="relative">
                <div className="text-white opacity-70 text-sm font-medium mb-1">Coverage</div>
                <div className="text-4xl font-bold text-white">0.7%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Feature-by-Feature Rollout */}
      <div className="space-y-4">
        {featureRollouts.map((rollout: any) => (
          <div
            key={rollout.feature}
            className="relative overflow-hidden rounded-xl bg-slate-800/50 backdrop-blur-sm border border-slate-700/50"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className={`p-3 bg-gradient-to-br ${rollout.color} rounded-lg`}>
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <h4 className="text-lg font-semibold text-white">{rollout.feature}</h4>
                      <span className="px-2 py-1 bg-slate-700/50 text-slate-300 text-xs font-medium rounded border border-slate-600/50">
                        {rollout.phase}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-400">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        <span>
                          {rollout.enabled} / {rollout.total} companies ({rollout.percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`px-4 py-2 rounded-lg border flex items-center gap-2 ${getStatusColor(rollout.status)}`}>
                  {getStatusIcon(rollout.status)}
                  <span className="font-medium capitalize">{rollout.status}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="relative">
                <div className="h-3 bg-slate-900 rounded-full overflow-hidden border border-slate-700">
                  <div
                    className={`h-full bg-gradient-to-r ${rollout.color} transition-all duration-500`}
                    style={{ width: `${rollout.percentage}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-xs text-slate-500">
                  <span>0%</span>
                  <span>25%</span>
                  <span>50%</span>
                  <span>75%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Rollout Stages */}
              {rollout.status !== 'planned' && (
                <div className="mt-4 grid grid-cols-4 gap-3">
                  <div className={`p-3 rounded-lg border ${rollout.percentage >= 1 ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-900/50 border-slate-700/30'}`}>
                    <div className="text-xs text-slate-400 mb-1">Canary (1%)</div>
                    {rollout.percentage >= 1 ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-slate-500" />
                    )}
                  </div>

                  <div className={`p-3 rounded-lg border ${rollout.percentage >= 10 ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-900/50 border-slate-700/30'}`}>
                    <div className="text-xs text-slate-400 mb-1">10%</div>
                    {rollout.percentage >= 10 ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-slate-500" />
                    )}
                  </div>

                  <div className={`p-3 rounded-lg border ${rollout.percentage >= 50 ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-900/50 border-slate-700/30'}`}>
                    <div className="text-xs text-slate-400 mb-1">50%</div>
                    {rollout.percentage >= 50 ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-slate-500" />
                    )}
                  </div>

                  <div className={`p-3 rounded-lg border ${rollout.percentage >= 100 ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-900/50 border-slate-700/30'}`}>
                    <div className="text-xs text-slate-400 mb-1">100%</div>
                    {rollout.percentage >= 100 ? (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    ) : (
                      <Clock className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
